import React, { useEffect } from 'react';
import { Modal, Button } from "antd";
import { WarningOutlined } from '@ant-design/icons';
import "./style.css";

export const UIModal = (props: any) => {
  //destructuring props
  const { visible, handleCancel, handleOk, title, footer, className, width } = props;

  return (
    <>
          <Modal
            wrapClassName={className ? `${className} modalClass` : "modalClass"}
            title={title}
            visible={visible}
            footer={footer}
            onCancel={handleCancel}
            onOk={handleOk}
            closable={false}
            width={width}
          >
            {props.children}
          </Modal>
    </>
  );
};

// ProcessRunningModal Interface
interface ProcessRunningModalProps {
  visible: boolean;
  onClose: () => void;
  processName?: string;
  deviceName?: string;
  errorMessage?: string;
}

// ProcessRunningModal Component
export const ProcessRunningModal: React.FC<ProcessRunningModalProps> = ({
  visible,
  onClose,
  processName = "Process",
  deviceName = "Device",
  errorMessage
}) => {
  // Log modal state changes
  useEffect(() => {
    if (visible) {
      console.log('ProcessRunningModal: Modal opened', {
        processName,
        deviceName,
        timestamp: new Date().toISOString()
      });
    } else {
      console.log('ProcessRunningModal: Modal closed', {
        timestamp: new Date().toISOString()
      });
    }
  }, [visible, processName, deviceName]);

  // Log component mount/unmount
  useEffect(() => {
    console.log('ProcessRunningModal: Component mounted');
    
    return () => {
      console.log('ProcessRunningModal: Component unmounted');
    };
  }, []);

  // Handle modal close with logging
  const handleClose = () => {
    console.log('ProcessRunningModal: Close button clicked', {
      processName,
      deviceName,
      timestamp: new Date().toISOString()
    });
    onClose();
  };

  // Log render
  console.log('ProcessRunningModal: Rendering modal', {
    visible,
    processName,
    deviceName
  });

  // Determine if this is an error modal or "already running" modal
  const isErrorModal = !!errorMessage;
  const isAlreadyRunning = errorMessage && typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('already running');
  const isProcessNotRunning = errorMessage && typeof errorMessage === 'string' && (
    errorMessage.toLowerCase().includes('not running') ||
    errorMessage.toLowerCase().includes('process not found')
  );
  const isEmptyReferenceStock = errorMessage && typeof errorMessage === 'string' && (
    errorMessage.toLowerCase().includes('set the reference stock') ||
    errorMessage.toLowerCase().includes('reference stock is empty') ||
    errorMessage.toLowerCase().includes('please set the reference stock')
  );

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={handleClose}
      footer={null}
      centered
      className="process-running-modal"
      width={400}
      closable={false}
    >
      <div className="process-running-content">
        <div className="modal-header">
          <div className="warning-icon">
            <WarningOutlined />
          </div>
          <h3 className="modal-title">
            {isEmptyReferenceStock ? "Reference Stock Required" :
             isProcessNotRunning ? "Process Not Running" :
             isErrorModal ? "Process Error" : "Process Already Running"}
          </h3>
        </div>
        
        <div className="modal-body">
          <p className="modal-message">
            {isEmptyReferenceStock ? (
              <>
                {errorMessage || "Please set the reference stock before starting the process."}
              </>
            ) : isProcessNotRunning ? (
              <>
                The {processName} process is not currently running on {deviceName}.
                <br /><br />
                Please try starting the process again.
              </>
            ) : isErrorModal ? (
              <>
                {isAlreadyRunning ? (
                  <>
                    A {processName} is already running on {deviceName}. 
                    Please wait for the current process to complete before starting a new one.
                  </>
                ) : (
                  <>
                    {(() => {
                      // Sanitize error message - remove HTML tags if present
                      let cleanMessage = String(errorMessage || 'Unknown error');
                      // Check if message contains HTML (safeguard in case HTML wasn't sanitized earlier)
                      if (cleanMessage.trim().startsWith('<html') || cleanMessage.includes('<title>')) {
                        // Extract text from HTML or provide default message
                        const statusMatch = cleanMessage.match(/<title>(\d+)\s+([^<]+)<\/title>/i);
                        if (statusMatch) {
                          const htmlStatus = statusMatch[1];
                          const htmlError = statusMatch[2];
                          if (htmlStatus === '504' || htmlError?.toLowerCase().includes('gateway time-out')) {
                            cleanMessage = "The request timed out. The server may be temporarily unavailable. Please try again in a few moments.";
                          } else {
                            cleanMessage = `Server error (${htmlStatus}): ${htmlError || 'The server encountered an error'}. Please try again later.`;
                          }
                        } else {
                          cleanMessage = "The server is temporarily unavailable. Please try again in a few moments.";
                        }
                      }
                      return (
                        <>
                          {cleanMessage}
                          <br /><br />
                          Process: {processName} on {deviceName}
                        </>
                      );
                    })()}
                  </>
                )}
              </>
            ) : (
              <>
                A {processName} is already running on {deviceName}. 
                Please wait for the current process to complete before starting a new one.
              </>
            )}
          </p>
        </div>
        
        <div className="modal-footer">
          <Button 
            type="primary" 
            onClick={handleClose}
            className="okay-button"
            size="large"
          >
            Okay
          </Button>
        </div>
      </div>
    </Modal>
  );
};
